// Listing search for the Matches tab discovery surface ("הוספת נכסים").
//
// HISTORY — why this file was rewritten (2026-08-10). It shipped as
// `matchesAlgolia.ts`, querying Algolia through the per-user secured key. That
// was already dead on arrival: the dashboard cut over to the server's
// Postgres-backed search on 2026-08-03 (`USE_POSTGRES_LISTINGS_SEARCH = true`,
// utils/listingsSearchApi.ts), and in that mode AlgoliaClientContext hands out
// `index: null` BY DESIGN — the browser must never again mint a key that
// exposes the whole scraped corpus, seller phone numbers included. The NEW CRM
// branch merged six days later and git merged it cleanly (different files), so
// the tab's search effect simply returned early on the null index forever:
// permanent skeletons, no error, no retry, nothing in the console.
//
// So: same public surface (MatchHit, searchMatchListings, segmentMatchHits,
// findYad2MadlanDuplicateIds), Postgres underneath.
//
// WHY ONE REQUEST PER ORIGIN, not one global request.
// The PG endpoint takes `locations[]` natively, so the old (origin × location)
// fan-out collapses — but NOT all the way to a single call. This surface splits
// its results into שלי / משרד / מהשוק, and PG sorts globally by updatedTime
// desc. One request would let the ~750k-row scraped market corpus fill the page
// and crowd the realtor's own handful of listings out entirely — "הנכסים שלי"
// would read 0 while the realtor has matches sitting right there. Three
// requests (one per origin, in parallel) guarantee every segment its own slots,
// and as a bonus make the per-origin totals EXACT instead of Algolia's summed
// nbHits, which double-counted a listing matching two location sub-queries.
//
// WHAT MOVED TO THE SERVER, and must not be re-implemented here:
// - webot ownership/org scoping — `buildScopePredicate` server-side. (Under
//   Algolia this was embedded in the secured key; either way, never a client
//   filter term. Do NOT add originalListingOwnersId / …OrganizationId
//   constraints to the request.)
// - the 3-day staleness cut WITH the webot exemption — one
//   `hidePotentiallyRemovedListings` boolean now; the server knows own listings
//   legitimately go months without an edit.
// - `listedFor: 'sale'` meaning "NOT rent" (older sale listings carry no
//   listingListedFor at all — 317,908 of 747,249 rows), which the endpoint
//   implements as `IS DISTINCT FROM 'rent'`. Same semantics the filter string
//   used to spell out.
import {
  rooms,
  parkingSpots,
  ListingOrigins
} from "../../../../../../@types/sharedSchema";
import { postListingsSearch } from "utils/listingsSearchApi";
import { buildActiveDateRange } from "components/_dashboard/common/DateRangeFilter";
import { tickedAmenities } from "utils/contacts/amenityLabels";
import type {
  ListingOrigin,
  ProcessedListingData,
  ProcessedListingAddress,
  REST_schema
} from "../../../../../../@types/sharedSchema";
import type { MatchFilters } from "./contactMatchFilters";

export type MatchHit = ProcessedListingData & { objectID: string };

// Third-party (yad2/madlan) listings not updated in this window are treated as
// potentially removed and hidden — the legacy hidePotentiallyRemovedListings default,
// always-on here (webot listings are the realtor's own and are never age-hidden).
// The window itself lives server-side now; this constant stays as the documented
// meaning of the flag for the filters drawer's copy.
export const STALE_LISTING_DAYS = 3;

// Per-origin page size. The Algolia fan-out asked for 60 per (origin × location),
// so a 3-location search could return 180 for one origin; locations now ride a
// single request, and this scaling keeps that ceiling rather than silently
// shrinking multi-area searches to 60. 300 is the endpoint's own cap.
const HITS_PER_ORIGIN = 60;
const MAX_HITS_PER_ORIGIN = 300;

// ── request building ────────────────────────────────────────────────────────────

/**
 * MatchFilters → one `ListingsSearchRequest` for a single origin.
 *
 * Covers BOTH tiers of MatchFilters: the profile-mappable filters the chip row edits
 * and the advanced ones the full filters drawer adds. Every guard below mirrors the
 * listings page's own `buildPgSearchRequest` (listingsMatch/algoliaClient.ts), so the
 * same control filters identically on both surfaces — that is the whole point of
 * reusing the drawer.
 *
 * One SearchState filter is deliberately absent and is frozen out of the CRM drawer
 * instead (see CRM_FROZEN_FILTERS in matchFiltersAdapter.ts):
 * - `listingOrigins` — this surface always queries all three origins and splits them
 *   into the שלי / משרד / מהשוק tab strip; an origin checkbox would silently empty a
 *   tab the strip still advertises a count for.
 */
export function buildMatchSearchRequest(
  filters: MatchFilters,
  origin: ListingOrigin,
  { queryText = "" }: { queryText?: string } = {}
): REST_schema.ListingsSearchRequest {
  const locations = filters.locations ?? [];
  const request: REST_schema.ListingsSearchRequest = {
    listingListedFor: filters.listedFor === "rent" ? "rent" : "sale",
    listingOrigins: [origin],
    maxResults: Math.min(
      HITS_PER_ORIGIN * Math.max(1, locations.length),
      MAX_HITS_PER_ORIGIN
    ),
    page: 0
  };

  if (queryText.trim()) request.searchTerm = queryText.trim();

  if (locations.length > 0) {
    // A canonical id (set by the locations autocomplete) beats the raw strings —
    // it also matches child neighborhoods and alternate spellings of the same
    // place. Absent fields must stay ABSENT, never empty strings: an empty
    // `street` would be filtered on as a literal.
    request.locations = locations.map((location) => ({
      ...(location.city ? { city: location.city } : {}),
      ...(location.neighborhood ? { neighborhood: location.neighborhood } : {}),
      ...(location.street ? { street: location.street } : {}),
      ...(location.canonicalLocationId
        ? { canonicalLocationId: location.canonicalLocationId }
        : {}),
      ...(location.canonicalLevel ? { canonicalLevel: location.canonicalLevel } : {})
    }));
  }

  // typeof guards, not just `!== undefined`: contact docs have carried STRING values
  // in the numeric fields (bad imports), and a string here rides into the request and
  // fails the endpoint's zod z.number() — a silent 400 that renders as zero matches.
  if (typeof filters.minRooms === "number" && filters.minRooms >= 0) {
    request.minRooms = filters.minRooms;
  }
  // At the slider maximum "max rooms" means no cap — sending it would exclude
  // anything above the top notch.
  if (
    typeof filters.maxRooms === "number" &&
    filters.maxRooms >= 0 &&
    filters.maxRooms < rooms[rooms.length - 1]
  ) {
    request.maxRooms = filters.maxRooms;
  }
  if (typeof filters.minFloors === "number" && filters.minFloors >= -4) {
    request.minFloor = filters.minFloors;
  }
  if (typeof filters.maxFloors === "number" && filters.maxFloors <= 50) {
    request.maxFloor = filters.maxFloors;
  }
  // Built area. `> 0`, not `>= 0`: a 0 is an unset bound, and `square_meter >= 0` would still
  // drop the listings that state no size at all for a filter the realtor never set.
  if (typeof filters.minSquareMeter === "number" && filters.minSquareMeter > 0) {
    request.minSquareMeter = filters.minSquareMeter;
  }
  if (typeof filters.maxSquareMeter === "number" && filters.maxSquareMeter > 0) {
    request.maxSquareMeter = filters.maxSquareMeter;
  }
  if (typeof filters.minPrice === "number" && filters.minPrice >= 0) {
    request.minPrice = filters.minPrice;
  }
  if (typeof filters.maxPrice === "number" && filters.maxPrice >= 0) {
    request.maxPrice = filters.maxPrice;
  }
  // Only meaningful alongside a bound — a budget is what it widens. The server
  // ignores it otherwise, so sending it anyway would only make two identical
  // searches look different in the audit log.
  if (
    filters.includeListingsWithoutPrice &&
    (request.minPrice !== undefined || request.maxPrice !== undefined)
  ) {
    request.includeListingsWithoutPrice = true;
  }
  if (filters.propertyTypes && filters.propertyTypes.length > 0) {
    request.propertyTypes = filters.propertyTypes;
  }

  // A real `true` or not sent (`tickedAmenities`): the endpoint refuses the whole search over
  // one value that is not a boolean.
  const booleanFilters = tickedAmenities(filters.booleanProps);
  if (Object.keys(booleanFilters).length > 0) {
    // The endpoint validates every key against its own amenity whitelist.
    request.booleanFilters = booleanFilters;
  }

  // ── advanced tier (drawer-only) ───────────────────────────────────────────────
  if (filters.minParkingSpots !== undefined && filters.minParkingSpots >= 0) {
    request.minParkingSpots = filters.minParkingSpots;
  }
  if (
    filters.maxParkingSpots !== undefined &&
    filters.maxParkingSpots >= 0 &&
    filters.maxParkingSpots < parkingSpots[parkingSpots.length - 1]
  ) {
    request.maxParkingSpots = filters.maxParkingSpots;
  }
  if (filters.adTypes && filters.adTypes.length > 0) request.adTypes = filters.adTypes;
  if (filters.agencyNames && filters.agencyNames.length > 0) {
    request.agencyNames = filters.agencyNames;
  }
  if (filters.sellerNames && filters.sellerNames.length > 0) {
    request.sellerNames = filters.sellerNames;
  }
  if (filters.isAssetExclusive) request.isAssetExclusive = true;
  if (filters.onlyPriceChanged) request.onlyPriceChanged = true;

  // Undefined means ON — the historical always-on behaviour; the drawer's toggle is
  // what can switch it off. The webot exemption is applied server-side, so this is a
  // single flag rather than the old per-origin filter term.
  if (filters.hidePotentiallyRemovedListings !== false) {
    request.hidePotentiallyRemovedListings = true;
  }

  if (filters.publishedDateRange) {
    const { from, to } = buildActiveDateRange(
      filters.publishedDateRange.preset,
      filters.publishedDateRange.customFrom,
      filters.publishedDateRange.customTo
    );
    if (from) request.publishedFromMs = from.getTime();
    if (to) request.publishedToMs = to.getTime();
  }

  return request;
}

// ── hit parsing ─────────────────────────────────────────────────────────────────

// Dates arrive as epoch millis (the endpoint deliberately reproduces the wire shape
// Algolia hits had); anything whose key mentions "time" is rehydrated to a Date.
function convertNumberTimesToDates(obj: Record<string, unknown>): Record<string, unknown> {
  const result: Record<string, unknown> = { ...obj };
  for (const key in obj) {
    const value = obj[key];
    if (value && typeof value === "number" && key.toLowerCase().includes("time")) {
      result[key] = new Date(value);
    }
  }
  return result;
}

/**
 * Server hit → MatchHit. `objectID` is populated from the doc id because every
 * consumer on this surface (dedupe, selection, the favorites reconciliation, the
 * blacklist) keys on it — it is the Algolia-era name for the listing id and is kept
 * rather than renamed across ~9 files.
 */
export function parseListingHit(hit: REST_schema.ListingsSearchHit): MatchHit {
  return {
    ...(convertNumberTimesToDates(hit as unknown as Record<string, unknown>) as MatchHit),
    id: hit.id,
    objectID: hit.id
  };
}

// ── search ──────────────────────────────────────────────────────────────────────

export type MatchSearchResult = {
  hits: MatchHit[];
  /** Per-origin match totals. Exact below the endpoint's count cap; at the cap it
   * reports the cap, which still reads correctly as "more than we loaded". */
  originTotals: Record<ListingOrigin, number>;
};

/**
 * One search pass: three per-origin requests in parallel, deduped by id. A failed
 * request degrades to zero results for that origin instead of failing the whole pass
 * (a market outage must not blank out the realtor's own listings). `queryText` is the
 * free-text search-bar input, forwarded as the endpoint's `searchTerm`.
 */
export async function searchMatchListings(
  filters: MatchFilters,
  { queryText = "" }: { queryText?: string } = {}
): Promise<MatchSearchResult> {
  const responses = await Promise.all(
    ListingOrigins.map(async (origin) => {
      try {
        const response = await postListingsSearch(
          buildMatchSearchRequest(filters, origin, { queryText })
        );
        return { origin, response };
      } catch (err) {
        // Zero results for one origin is the graceful degradation — log and move on.
        console.error(`MatchesTab: listings search failed for origin ${origin}`, err);
        return { origin, response: null };
      }
    })
  );

  const seenIds = new Set<string>();
  const hits: MatchHit[] = [];
  const originTotals: Record<ListingOrigin, number> = { yad2: 0, webot: 0, madlan: 0 };

  for (const { origin, response } of responses) {
    if (!response) continue;
    originTotals[origin] += response.totalHits ?? 0;
    for (const rawHit of response.hits ?? []) {
      if (!rawHit?.id || seenIds.has(rawHit.id)) continue;
      seenIds.add(rawHit.id);
      hits.push(parseListingHit(rawHit));
    }
  }

  return { hits, originTotals };
}

// ── segmentation (ported from ListingsMatchContainer.getFilteredListings) ───────

export type MatchSegmentId = "mine" | "office" | "market";

export type SegmentedMatchHits = Record<MatchSegmentId, MatchHit[]>;

/**
 * מהנכסים שלי / מנכסי המשרד / מהשוק discrimination:
 * - mine:   webot origin AND originalListingOwnersId contains the user email.
 * - office: webot origin AND the listing's org is one of the user's orgs (own listings
 *           excluded here so the segments don't overlap — a deliberate refinement of the
 *           legacy tabs, where "myOrganization" also contained the user's own listings).
 * - market: yad2 / madlan scraped listings.
 */
export function segmentMatchHits(
  hits: MatchHit[],
  { userEmail, organizationIds }: { userEmail: string; organizationIds: string[] }
): SegmentedMatchHits {
  const mine: MatchHit[] = [];
  const office: MatchHit[] = [];
  const market: MatchHit[] = [];
  for (const hit of hits) {
    if (hit.listingOrigin === "webot") {
      if (hit.originalListingOwnersId?.includes(userEmail)) mine.push(hit);
      else if (
        hit.originalListingOrganizationId &&
        organizationIds.includes(hit.originalListingOrganizationId)
      ) {
        office.push(hit);
      } else {
        // webot listing the server scoped us into but which isn't attributable —
        // treat as office-adjacent rather than dropping it silently.
        office.push(hit);
      }
    } else {
      market.push(hit);
    }
  }
  return { mine, office, market };
}

/**
 * yad2-vs-madlan dedupe (port of isDuplicateMadlanListing, with the field paths FIXED —
 * the legacy version compared root-level city/rooms fields that don't exist on
 * ProcessedListingData, which made it effectively price-only). A yad2 listing counts as
 * a duplicate when a madlan hit shares city+street+houseNumber+price+roomsCount.
 * Duplicates are kept (sorted last), not dropped — same UX as the legacy list.
 */
export function findYad2MadlanDuplicateIds(hits: MatchHit[]): Set<string> {
  const dedupeKey = (h: MatchHit): string =>
    [
      h.address?.city ?? "",
      h.address?.street ?? "",
      h.address?.houseNumber ?? "",
      h.price ?? "",
      h.roomsCount ?? ""
    ].join("|");
  const madlanKeys = new Set(
    hits.filter((h) => h.listingOrigin === "madlan").map((h) => dedupeKey(h))
  );
  const duplicates = new Set<string>();
  for (const hit of hits) {
    if (hit.listingOrigin === "yad2" && madlanKeys.has(dedupeKey(hit))) {
      duplicates.add(hit.objectID);
    }
  }
  return duplicates;
}

// ── location autocomplete ───────────────────────────────────────────────────────

/**
 * Canonical-registry suggestion → the `ProcessedListingAddress` shape MatchFilters
 * stores. The caller owns the fetch (it needs the WebotServer context, which is a
 * hook) — this is just the mapping, kept next to the request builder that consumes
 * `canonicalLocationId`.
 *
 * Absent fields stay ABSENT rather than becoming empty strings: the registry
 * populates only the fields belonging to a suggestion's level (a street carries city
 * + street, never a neighborhood), and an empty string would be sent as a literal
 * filter value.
 */
export function locationSuggestionToAddress(
  suggestion: REST_schema.LocationSuggestion
): ProcessedListingAddress {
  return {
    ...(suggestion.city ? { city: suggestion.city } : {}),
    ...(suggestion.neighborhood ? { neighborhood: suggestion.neighborhood } : {}),
    ...(suggestion.street ? { street: suggestion.street } : {}),
    canonicalLocationId: suggestion.locationId,
    canonicalLevel: suggestion.level
  } as ProcessedListingAddress;
}
